import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Check,
  Image as ImageIcon,
  Plus,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { toast } from '../../../ui/toast';
import {
  useCreateCCAQuestionMutation,
  useGetStorageUrlQuery,
  usePresignCCAImageUploadMutation,
  useUpdateCCAQuestionMutation,
} from '../../../utils/api_service/ccaApi';
import {
  CCAQuestion,
  CreateCCAQuestionRequest,
} from '../../../utils/types/cca_types';
import { CCA_LEVEL_LABELS, CCA_SUBDOMAINS } from './ccaConstants';

// ─── Types ──────────────────────────────────────────────────────────────────

interface OptionDraft {
  text: string;
  isCorrect: boolean;
  imageKey?: string;
  imagePreview?: string;
}

interface FormState {
  subdomain: string;
  level: number;
  questionType: 'multiple-choice' | 'short-answer';
  questionText: string;
  questionImageKey?: string;
  questionImagePreview?: string;
  options: OptionDraft[];
  idealAnswer: string;
}

const INITIAL_FORM: FormState = {
  subdomain: CCA_SUBDOMAINS[0],
  level: 1,
  questionType: 'multiple-choice',
  questionText: '',
  options: [
    { text: '', isCorrect: false },
    { text: '', isCorrect: false },
  ],
  idealAnswer: '',
};

// ─── SignedImageDisplay Component ────────────────────────────────────────────

interface SignedImageDisplayProps {
  imageKey: string;
  alt?: string;
  fallbackText?: string;
}

const SignedImageDisplay: React.FC<SignedImageDisplayProps> = ({
  imageKey,
  alt = 'Image',
  fallbackText,
}) => {
  const { data, isLoading, isError } = useGetStorageUrlQuery(imageKey);

  if (isLoading) {
    return (
      <Paper
        variant='outlined'
        sx={{
          width: 56,
          height: 56,
          borderRadius: 2,
          bgcolor: 'grey.100',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={16} />
      </Paper>
    );
  }

  if (isError || !data?.url) {
    return (
      <Paper
        variant='outlined'
        sx={{
          height: 56,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          borderRadius: 2,
          bgcolor: 'grey.100',
        }}
      >
        <ImageIcon size={16} />
        <Typography
          variant='caption'
          color='text.secondary'
          noWrap
          sx={{ maxWidth: 80 }}
        >
          {fallbackText || imageKey.split('/').pop()}
        </Typography>
      </Paper>
    );
  }

  return (
    <Box
      component='img'
      src={data.url}
      alt={alt}
      sx={{
        width: 56,
        height: 56,
        borderRadius: 2,
        objectFit: 'cover',
        border: '1px solid',
        borderColor: 'divider',
      }}
    />
  );
};

// ─── Validation ─────────────────────────────────────────────────────────────

function validate(form: FormState): string | null {
  if (!form.questionText.trim()) return 'Question text is required.';
  if (form.questionText.trim().length < 10)
    return 'Question text must be at least 10 characters.';
  if (!form.idealAnswer.trim()) return 'Ideal answer is required.';
  if (form.questionType === 'multiple-choice') {
    const filled = form.options.filter((o) => o.text.trim());
    if (filled.length < 2) return 'At least 2 options are required.';
    if (!filled.some((o) => o.isCorrect))
      return 'At least one option must be marked as correct.';
  }
  return null;
}

// ─── ImageUploadField ────────────────────────────────────────────────────────

interface ImageUploadFieldProps {
  imageKey?: string;
  imagePreview?: string;
  isUploading: boolean;
  onUpload: (file: File) => void;
  onClear: () => void;
  label?: string;
}

const ImageUploadField: React.FC<ImageUploadFieldProps> = ({
  imageKey,
  imagePreview,
  isUploading,
  onUpload,
  onClear,
  label = '+ Add image',
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const hasImage = !!(imageKey || imagePreview);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
      {imagePreview ? (
        <Box sx={{ position: 'relative', flexShrink: 0 }}>
          <Box
            component='img'
            src={imagePreview}
            alt='Preview'
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              objectFit: 'cover',
              border: '1px solid',
              borderColor: 'divider',
            }}
          />
          <IconButton
            size='small'
            onClick={onClear}
            sx={{
              position: 'absolute',
              top: -6,
              right: -6,
              width: 20,
              height: 20,
              bgcolor: 'error.main',
              color: '#ffffff',
              '&:hover': { bgcolor: 'error.dark' },
            }}
          >
            <X size={12} />
          </IconButton>
        </Box>
      ) : imageKey ? (
        <Box sx={{ position: 'relative', flexShrink: 0 }}>
          <SignedImageDisplay imageKey={imageKey} />
          <IconButton
            size='small'
            onClick={onClear}
            sx={{
              position: 'absolute',
              top: -6,
              right: -6,
              width: 20,
              height: 20,
              bgcolor: 'error.main',
              color: '#ffffff',
              '&:hover': { bgcolor: 'error.dark' },
            }}
          >
            <X size={12} />
          </IconButton>
        </Box>
      ) : null}

      <Button
        component='label'
        variant='outlined'
        size='small'
        disabled={isUploading}
        startIcon={
          isUploading ? <CircularProgress size={14} /> : <Upload size={14} />
        }
        sx={{
          textTransform: 'none',
          borderColor: 'divider',
          color: 'text.secondary',
          borderRadius: 2,
          borderStyle: 'dashed',
          '&:hover': {
            borderColor: 'primary.main',
            color: 'primary.main',
          },
        }}
      >
        {isUploading ? 'Uploading…' : hasImage ? 'Change image' : label}
        <input
          ref={inputRef}
          type='file'
          accept='image/*'
          hidden
          disabled={isUploading}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              onUpload(file);
              e.target.value = '';
            }
          }}
        />
      </Button>
    </Box>
  );
};

// ─── Component ──────────────────────────────────────────────────────────────

interface Props {
  isOpen: boolean;
  onClose: () => void;
  question?: CCAQuestion;
}

const toFormSubdomain = (snake: string): string =>
  snake
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const toFormType = (apiType: string): 'multiple-choice' | 'short-answer' =>
  apiType === 'multiple_choice' ? 'multiple-choice' : 'short-answer';

export const CreateQuestionDialog: React.FC<Props> = ({
  isOpen,
  onClose,
  question,
}) => {
  const isEditMode = !!question;

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [errors, setErrors] = useState<string | null>(null);
  const [uploadingTarget, setUploadingTarget] =
    useState<'question' | number | null>(null);

  const [createQuestion, { isLoading: isCreating }] =
    useCreateCCAQuestionMutation();
  const [updateQuestion, { isLoading: isUpdating }] =
    useUpdateCCAQuestionMutation();
  const [presign] = usePresignCCAImageUploadMutation();
  const isLoading = isCreating || isUpdating;

  useEffect(() => {
    if (isOpen && question) {
      setForm({
        subdomain: toFormSubdomain(question.subdomain),
        level: question.level,
        questionType: toFormType(question.questionType),
        questionText: question.question.text,
        questionImageKey: question.question.imageKey,
        questionImagePreview: undefined,
        options:
          question.options.length > 0
            ? question.options.map((o) => ({
              text: o.text,
              isCorrect: o.isCorrect,
              imageKey: o.imageKey,
              imagePreview: undefined,
            }))
            : [
              { text: '', isCorrect: false },
              { text: '', isCorrect: false },
            ],
        idealAnswer: question.idealAnswer ?? '',
      });
    } else if (isOpen && !question) {
      setForm(INITIAL_FORM);
    }
    setErrors(null);
  }, [isOpen, question]);

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setErrors(null);
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // ─── Image upload ────────────────────────────────────────────────────────

  const handleUploadImage = async (file: File, target: 'question' | number) => {
    setUploadingTarget(target);
    try {
      const response = await presign(file.name).unwrap();
      const uploadUrl: string = response.url;
      const imageKey: string = response.imageKey;

      const putResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
      });
      if (!putResponse.ok) {
        throw new Error(`S3 upload rejected: ${putResponse.status}`);
      }

      const preview = URL.createObjectURL(file);

      if (target === 'question') {
        setForm((prev) => ({
          ...prev,
          questionImageKey: imageKey,
          questionImagePreview: preview,
        }));
      } else {
        setForm((prev) => ({
          ...prev,
          options: prev.options.map((o, i) =>
            i === target ? { ...o, imageKey, imagePreview: preview } : o,
          ),
        }));
      }
    } catch {
      toast.error('Image upload failed.', { position: 'bottom-center' });
    } finally {
      setUploadingTarget(null);
    }
  };

  const clearQuestionImage = () =>
    setForm((prev) => ({
      ...prev,
      questionImageKey: undefined,
      questionImagePreview: undefined,
    }));

  const clearOptionImage = (index: number) =>
    setForm((prev) => ({
      ...prev,
      options: prev.options.map((o, i) =>
        i === index
          ? { ...o, imageKey: undefined, imagePreview: undefined }
          : o,
      ),
    }));

  const handleOptionChange = (
    index: number,
    field: keyof OptionDraft,
    value: string | boolean,
  ) => {
    setErrors(null);
    setForm((prev) => {
      const options = prev.options.map((o, i) =>
        i === index ? { ...o, [field]: value } : o,
      );
      return { ...prev, options };
    });
  };

  const handleMarkCorrect = (index: number) => {
    setErrors(null);
    setForm((prev) => ({
      ...prev,
      options: prev.options.map((o, i) => ({ ...o, isCorrect: i === index })),
    }));
  };

  const addOption = () => {
    if (form.options.length >= 6) return;
    setForm((prev) => ({
      ...prev,
      options: [...prev.options, { text: '', isCorrect: false }],
    }));
  };

  const removeOption = (index: number) => {
    if (form.options.length <= 2) return;
    setForm((prev) => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index),
    }));
  };

  const handleQuestionTypeChange = (
    type: 'multiple-choice' | 'short-answer',
  ) => {
    setErrors(null);
    setForm((prev) => ({
      ...prev,
      questionType: type,
      options:
        type === 'multiple-choice'
          ? [
            { text: '', isCorrect: false },
            { text: '', isCorrect: false },
          ]
          : [],
    }));
  };

  const handleClose = () => {
    setForm(INITIAL_FORM);
    setErrors(null);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const error = validate(form);
    if (error) {
      setErrors(error);
      return;
    }

    const payload: CreateCCAQuestionRequest = {
      subdomain: form.subdomain.toLowerCase().replace(/ /g, '_'),
      level: form.level,
      questionType: form.questionType.replace(/-/g, '_'),
      question: {
        text: form.questionText.trim(),
        ...(form.questionImageKey && { imageKey: form.questionImageKey }),
      },
      options:
        form.questionType === 'multiple-choice'
          ? form.options
            .filter((o) => o.text.trim())
            .map((o) => ({
              id: crypto.randomUUID(),
              text: o.text.trim(),
              isCorrect: o.isCorrect,
              ...(o.imageKey && { imageKey: o.imageKey }),
            }))
          : [],
      idealAnswer: form.idealAnswer.trim(),
    };

    try {
      if (isEditMode && question) {
        await updateQuestion({
          questionId: question.id,
          body: payload,
        }).unwrap();
        toast.success('Question updated successfully.', {
          position: 'bottom-center',
        });
      } else {
        await createQuestion(payload).unwrap();
        toast.success('Question created successfully.', {
          position: 'bottom-center',
        });
      }
      handleClose();
    } catch (err: unknown) {
      const serverMessage =
        (err as { data?: { error?: string } })?.data?.error ??
        (isEditMode
          ? 'Failed to update question. Please try again.'
          : 'Failed to create question. Please try again.');
      toast.error(serverMessage, { position: 'bottom-center' });
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth='md'
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          {isEditMode ? 'Edit CCA Question' : 'Create CCA Question'}
        </DialogTitle>

        <DialogContent dividers sx={{ py: 2.5 }}>
          <Stack spacing={2.5}>
            {/* Row 1: Subdomain + Level + Type */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                gap: 2,
              }}
            >
              <FormControl size='small' fullWidth>
                <InputLabel>Subdomain</InputLabel>
                <Select
                  value={form.subdomain}
                  label='Subdomain'
                  onChange={(e) => setField('subdomain', e.target.value)}
                >
                  {CCA_SUBDOMAINS.map((sd) => (
                    <MenuItem key={sd} value={sd}>
                      {sd}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size='small' fullWidth>
                <InputLabel>Level</InputLabel>
                <Select
                  value={form.level}
                  label='Level'
                  onChange={(e) => setField('level', Number(e.target.value))}
                >
                  {Array.from({ length: 10 }, (_, i) => i + 1).map((l) => (
                    <MenuItem key={l} value={l}>
                      {l} — {CCA_LEVEL_LABELS[l]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size='small' fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={form.questionType}
                  label='Type'
                  onChange={(e) =>
                    handleQuestionTypeChange(
                      e.target.value as 'multiple-choice' | 'short-answer',
                    )
                  }
                >
                  <MenuItem value='multiple-choice'>Multiple Choice</MenuItem>
                  <MenuItem value='short-answer'>Short Answer</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Question Text + Image */}
            <Box>
              <TextField
                label='Question Text'
                multiline
                rows={3}
                fullWidth
                value={form.questionText}
                onChange={(e) => setField('questionText', e.target.value)}
                placeholder='Enter the question...'
              />
              <ImageUploadField
                imageKey={form.questionImageKey}
                imagePreview={form.questionImagePreview}
                isUploading={uploadingTarget === 'question'}
                onUpload={(file) => handleUploadImage(file, 'question')}
                onClear={clearQuestionImage}
                label='+ Add question image'
              />
            </Box>

            {/* Multiple Choice Options */}
            {form.questionType === 'multiple-choice' && (
              <Box>
                <Typography
                  variant='caption'
                  fontWeight={700}
                  textTransform='uppercase'
                  color='text.secondary'
                  display='block'
                  sx={{ mb: 1 }}
                >
                  Answer Options{' '}
                  <Typography
                    component='span'
                    variant='caption'
                    color='text.disabled'
                    sx={{ textTransform: 'none', fontWeight: 400 }}
                  >
                    (click checkmark to mark correct)
                  </Typography>
                </Typography>

                <Stack spacing={1.5}>
                  {form.options.map((opt, i) => (
                    <Paper
                      key={i}
                      variant='outlined'
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: opt.isCorrect ? '#ecfdf5' : '#f8fafc',
                        borderColor: opt.isCorrect ? '#a7f3d0' : 'divider',
                      }}
                    >
                      <Stack direction='row' spacing={1} alignItems='center'>
                        <IconButton
                          size='small'
                          onClick={() => handleMarkCorrect(i)}
                          title='Mark as correct answer'
                          sx={{
                            width: 28,
                            height: 28,
                            bgcolor: opt.isCorrect ? '#10b981' : '#ffffff',
                            color: opt.isCorrect ? '#ffffff' : 'text.disabled',
                            border: '1px solid',
                            borderColor: opt.isCorrect ? '#10b981' : 'divider',
                            '&:hover': {
                              bgcolor: opt.isCorrect ? '#059669' : '#f1f5f9',
                            },
                          }}
                        >
                          <Check size={16} />
                        </IconButton>

                        <TextField
                          size='small'
                          fullWidth
                          value={opt.text}
                          onChange={(e) =>
                            handleOptionChange(i, 'text', e.target.value)
                          }
                          placeholder={`Option ${i + 1}`}
                          sx={{ bgcolor: '#ffffff' }}
                        />

                        {form.options.length > 2 && (
                          <IconButton
                            size='small'
                            color='error'
                            onClick={() => removeOption(i)}
                            aria-label={`Remove option ${i + 1}`}
                          >
                            <Trash2 size={16} />
                          </IconButton>
                        )}
                      </Stack>

                      <ImageUploadField
                        imageKey={opt.imageKey}
                        imagePreview={opt.imagePreview}
                        isUploading={uploadingTarget === i}
                        onUpload={(file) => handleUploadImage(file, i)}
                        onClear={() => clearOptionImage(i)}
                        label='+ Add option image'
                      />
                    </Paper>
                  ))}
                </Stack>

                {form.options.length < 6 && (
                  <Button
                    size='small'
                    startIcon={<Plus size={14} />}
                    onClick={addOption}
                    sx={{ mt: 1, textTransform: 'none', fontWeight: 600 }}
                  >
                    Add option
                  </Button>
                )}
              </Box>
            )}

            {/* Ideal Answer */}
            <Box>
              <TextField
                label='Ideal Answer'
                fullWidth
                size='small'
                value={form.idealAnswer}
                onChange={(e) => setField('idealAnswer', e.target.value)}
                placeholder={
                  form.questionType === 'multiple-choice'
                    ? 'Expected answer or explanation...'
                    : 'Model answer...'
                }
              />
            </Box>

            {/* Validation error */}
            {errors && <Alert severity='error'>{errors}</Alert>}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleClose}
            disabled={isLoading}
            variant='outlined'
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            type='submit'
            disabled={isLoading || uploadingTarget !== null}
            variant='contained'
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              bgcolor: '#10b981',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#059669', boxShadow: 'none' },
            }}
          >
            {isLoading
              ? isEditMode
                ? 'Saving...'
                : 'Creating...'
              : isEditMode
                ? 'Save Changes'
                : 'Create Question'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
